import { SubmissionPlacementTaskInfo } from '@common/types';
import { isEmpty, truncate } from 'lodash';
import { Dialog, List, Stack, Typography } from '@common-components';
import { PlacementTaskStatus } from 'enums';
import { messages } from 'i18n';
import { PartialSubmission } from 'types';
import { DialogContent, DialogHeader } from 'components/Dialog';
import { PlacementTaskItem } from './PlacementTaskItems';
import { usePlacementTasksManager } from './usePlacementTasksManager';

export interface PlacementProcessDialogDialogProps {
  submission: PartialSubmission;
  isOpen: boolean;
  onClose: () => void;
  onUpdateSuccess?: () => Promise<void>;
}

export function PlacementProcessDialog({
  submission,
  isOpen,
  onClose,
  onUpdateSuccess,
}: PlacementProcessDialogDialogProps) {
  const { groupedTasks, isUpdating, getNextStatus, showSkipButton, handleStatusChange, handleSkip } =
    usePlacementTasksManager({ placementTasks: submission.placementTasks || [], onUpdateSuccess });

  // Order: Completed, InProgress, Planned (includes Skipped)
  const statusOrder = [PlacementTaskStatus.Completed, PlacementTaskStatus.InProgress, PlacementTaskStatus.Planned];

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogHeader
        id="placement-process-dialog-header"
        title={messages.placementProcess.dialog.title}
        subtitle={truncate(submission.insuredName, { length: 90 })}
        onClose={onClose}
      />
      <DialogContent>
        <Stack gap={3}>
          {statusOrder.map((status) => {
            const tasks = (groupedTasks as Record<PlacementTaskStatus, SubmissionPlacementTaskInfo[]>)[status] || [];

            return (
              <Stack key={status}>
                <Typography
                  variant="h6"
                  sx={{
                    borderRadius: 1,
                  }}
                  color="text.secondary"
                  textTransform="uppercase"
                >
                  {messages.placementProcess.placementTaskStatus[status]}
                </Typography>
                {!isEmpty(tasks) ? (
                  <List sx={{ pt: 0 }}>
                    {tasks.map((task: SubmissionPlacementTaskInfo) => (
                      <PlacementTaskItem
                        key={`${task.placementTask}-${task.stepName}`}
                        task={task}
                        getNextStatus={getNextStatus}
                        showSkipButton={showSkipButton}
                        handleStatusChange={handleStatusChange}
                        handleSkip={handleSkip}
                        isUpdating={isUpdating}
                      />
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2, fontStyle: 'italic' }}>
                    {messages.placementProcess.dialog.noTasks(messages.placementProcess.placementTaskStatus[status])}
                  </Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
